import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, FolderKanban, Calendar, Loader2, ChevronDown, ChevronUp, MapPin, Users, Plus, FileText, ListChecks } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { projectService, profileService, quoteService } from '@/services';
import type { QuoteListRow } from '@/services/quoteService';
import { projectSegmentService, type ProjectSegmentWithRelations } from '@/services';
import ProjectSegmentFormDialog from '@/components/project/ProjectSegmentFormDialog';
import ManageSegmentsDialog from '@/components/project/ManageSegmentsDialog';
import AssignQuotesDialog from '@/components/project/AssignQuotesDialog';
import RecordMetadata from '@/components/RecordMetadata';
import { AGDataGrid } from '@/components/ui/ag-data-grid';
import { usePagination } from '@/hooks/usePagination';
import type { ColDef } from 'ag-grid-community';

interface ProjectWithRelations {
  id: string;
  name: string;
  description: string | null;
  region_id: string | null;
  project_manager: string | null;
  expected_start_date: string | null;
  expected_end_date: string | null;
  company_id: string;
  created: string | null;
  modified: string | null;
  created_by: string | null;
  updated_by: string | null;
  list_regions: { display_value: string } | null;
  manager_profile: { full_name: string | null } | null;
  created_by_profile: { full_name: string | null } | null;
  updated_by_profile: { full_name: string | null } | null;
  project_segments: { count: number }[];
}

export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [isHeaderOpen, setIsHeaderOpen] = useState(true);
  const [segmentDialogOpen, setSegmentDialogOpen] = useState(false);
  const [editingSegment, setEditingSegment] = useState<ProjectSegmentWithRelations | null>(null);
  const [manageSegmentsOpen, setManageSegmentsOpen] = useState(false);
  const [assignQuotesOpen, setAssignQuotesOpen] = useState(false);

  const quotesPagination = usePagination({
    storageKey: 'project_detail_quotes',
    persistToUrl: false,
  });

  useEffect(() => {
    quotesPagination.resetToFirstPage();
  }, [id]);

  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const result = await profileService.get(user.id);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!user?.id,
  });

  const { data: project, isLoading } = useQuery({
    queryKey: ['project', id],
    queryFn: async () => {
      if (!id) return null;
      const result = await projectService.getWithRelations(id);
      if (result.error) throw result.error;
      return result.data as unknown as ProjectWithRelations;
    },
    enabled: !!id,
  });

  const { data: segments = [], isLoading: segmentsLoading } = useQuery({
    queryKey: ['project-segments', id],
    queryFn: async () => {
      if (!id) return [];
      const result = await projectSegmentService.listByProject(id);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!id,
  });


  const { data: quotesResult, isLoading: quotesLoading, isFetching: quotesFetching } = useQuery({
    queryKey: ['project-quotes', id, quotesPagination.pageIndex, quotesPagination.pageSize, quotesPagination.debouncedSearch],
    queryFn: async () => {
      const result = await quoteService.listByProjectPaginated(
        id!,
        { page: quotesPagination.pageIndex, pageSize: quotesPagination.pageSize },
        { search: quotesPagination.debouncedSearch || undefined }
      );
      if (result.error) throw result.error;
      return result;
    },
    enabled: !!id,
  });

  const quoteColumnDefs = useMemo<ColDef<QuoteListRow>[]>(() => [
    {
      headerName: 'Vendor Name',
      flex: 1.5,
      valueGetter: (params) => params.data?.vendor?.name,
      valueFormatter: (p) => p.value || '—',
    },
    {
      headerName: 'Well Name',
      field: 'well_name',
      flex: 1.5,
      valueFormatter: (p) => p.value || '—',
    },
    {
      headerName: 'Ver#',
      field: 'version_number',
      width: 80,
    },
    {
      headerName: 'Status',
      width: 140,
      valueGetter: (params) => params.data?.status_name,
      cellRenderer: (params: any) =>
        params.value ? (
          <span className="badge badge-primary">{params.value}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      headerName: 'Total Price',
      field: 'total_price',
      flex: 1,
      type: 'rightAligned',
      valueFormatter: (p) => p.value != null
        ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(p.value)
        : '—',
    },
    {
      headerName: 'Effective',
      field: 'effective_date',
      flex: 1,
      valueFormatter: (p) => p.value ? format(parseISO(p.value), 'MM/dd/yyyy') : '—',
    },
    {
      headerName: 'Expires',
      field: 'expiration_date',
      flex: 1,
      valueFormatter: (p) => p.value ? format(parseISO(p.value), 'MM/dd/yyyy') : '—',
    },
    {
      headerName: 'Modified',
      field: 'quote_modified',
      flex: 1,
      valueFormatter: (p) => p.value ? format(parseISO(p.value), 'MM/dd/yyyy') : '—',
    },
  ], []);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return format(parseISO(dateStr), 'MMM d, yyyy');
  };

  const handleSaveSegment = async (data: { segment_id: string; segment_manager?: string | null; notes?: string }) => {
    if (!id || !profile?.company_id || !user?.id) return;

    if (editingSegment) {
      // Update existing segment
      const snapshot = {
        segment_id: editingSegment.segment_id,
        segment_manager: editingSegment.segment_manager,
        notes: editingSegment.notes,
      };
      const result = await projectSegmentService.update(
        editingSegment.id,
        { segment_id: data.segment_id, segment_manager: data.segment_manager || null, notes: data.notes || null },
        snapshot,
        { companyId: profile.company_id, userId: user.id }
      );
      if (result.error) {
        toast.error('Failed to update segment');
        throw result.error;
      }
      toast.success('Segment updated');
    } else {
      // Create new segment
      const result = await projectSegmentService.create(
        { project_id: id, segment_id: data.segment_id, segment_manager: data.segment_manager || null, notes: data.notes || null },
        { companyId: profile.company_id, userId: user.id }
      );
      if (result.error) {
        toast.error('Failed to add segment');
        throw result.error;
      }
      toast.success('Segment added');
    }

    queryClient.invalidateQueries({ queryKey: ['project-segments', id] });
    queryClient.invalidateQueries({ queryKey: ['project', id] });
  };

  const handleOpenAdd = () => {
    setEditingSegment(null);
    setSegmentDialogOpen(true);
  };

  const handleOpenEdit = (segment: ProjectSegmentWithRelations) => {
    setEditingSegment(segment);
    setSegmentDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Project not found.</p>
        <button onClick={() => navigate('/projects')} className="btn-primary mt-4">
          Back to Projects
        </button>
      </div>
    );
  }

  return (
    <div className="animate-slide-up">
      {/* Back Button */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate('/projects')}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Projects
        </button>
        <Button variant="outline" size="sm" onClick={() => navigate(`/projects/${id}/schedule`)}>
          <Calendar className="w-4 h-4 mr-2" />
          Schedule
        </Button>
      </div>

      {/* Header Card */}
      <div className="mb-6">
        <Collapsible open={isHeaderOpen} onOpenChange={setIsHeaderOpen}>
          <div className="bg-card border border-border rounded-lg">
            <CollapsibleTrigger asChild>
              <button className="w-full p-6 flex items-center justify-between hover:bg-muted/50 transition-colors rounded-lg">
                {isHeaderOpen ? (
                  <div className="flex items-start gap-4 text-left">
                    <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center">
                      <FolderKanban className="w-7 h-7 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h1 className="text-2xl font-bold text-foreground mb-1">{project.name}</h1>
                      <div className="flex items-center gap-2 text-muted-foreground flex-wrap">
                        {project.list_regions && (
                          <>
                            <MapPin className="w-4 h-4" />
                            <span>{project.list_regions.display_value}</span>
                          </>
                        )}
                        {project.manager_profile?.full_name && (
                          <>
                            {project.list_regions && <span>•</span>}
                            <Users className="w-4 h-4" />
                            <span>{project.manager_profile.full_name}</span>
                          </>
                        )}
                      </div>
                      {/* Segment Pills */}
                      {segments.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {segments.map((seg) => (
                            <Badge key={seg.id} variant="secondary">
                              {seg.list_segments?.display_value || 'Unknown'}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <FolderKanban className="w-5 h-5 text-primary" />
                    </div>
                    <span className="text-lg font-semibold text-foreground">
                      {project.name}
                      {project.list_regions && (
                        <span className="text-muted-foreground font-normal"> | {project.list_regions.display_value}</span>
                      )}
                    </span>
                    {segments.length > 0 && (
                      <div className="flex gap-1 ml-2">
                        {segments.map((seg) => (
                          <Badge key={seg.id} variant="secondary" className="text-xs">
                            {seg.list_segments?.display_value || 'Unknown'}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {isHeaderOpen ? (
                  <ChevronUp className="w-5 h-5 text-muted-foreground" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-muted-foreground" />
                )}
              </button>
            </CollapsibleTrigger>

            <CollapsibleContent>
              <div className="px-6 pb-6 pt-0 border-t border-border mx-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-6">
                  {project.description && (
                    <div className="col-span-2 md:col-span-4">
                      <label className="text-sm text-muted-foreground">Description</label>
                      <p className="mt-1 text-foreground">{project.description}</p>
                    </div>
                  )}
                  <div>
                    <label className="text-sm text-muted-foreground">Expected Start</label>
                    <div className="flex items-center gap-2 mt-1">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">{formatDate(project.expected_start_date)}</span>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground">Expected End</label>
                    <div className="flex items-center gap-2 mt-1">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">{formatDate(project.expected_end_date)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="segments" className="w-full">
        <TabsList className="w-full justify-start border-b border-border rounded-none bg-transparent h-auto p-0 mb-6">
          <TabsTrigger
            value="segments"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3"
          >
            Segments
          </TabsTrigger>
          <TabsTrigger
            value="quotes"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3"
          >
            Quotes
          </TabsTrigger>
        </TabsList>

        <TabsContent value="segments">
          <div className="flex justify-end gap-2 mb-4">
            <Button variant="outline" onClick={() => setManageSegmentsOpen(true)}>
              <ListChecks className="w-4 h-4 mr-2" />
              Manage Multiple Segments
            </Button>
            <Button onClick={handleOpenAdd}>
              <Plus className="w-4 h-4 mr-2" />
              Add Segment
            </Button>
          </div>

          {segmentsLoading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : segments.length === 0 ? (
            <div className="bg-card border border-border rounded-lg p-8 text-center text-muted-foreground">
              No segments added yet. Click "Add Segment" to get started.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {segments.map((seg) => (
                <SegmentCard key={seg.id} segment={seg} onClick={() => handleOpenEdit(seg)} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="quotes">
          <div className="flex justify-end mb-4">
            <Button onClick={() => setAssignQuotesOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Assign Quotes
            </Button>
          </div>
          <AGDataGrid<QuoteListRow>
            rowData={quotesResult?.data ?? []}
            columnDefs={quoteColumnDefs}
            totalCount={quotesResult?.totalCount ?? 0}
            pageIndex={quotesPagination.pageIndex}
            pageSize={quotesPagination.pageSize}
            pageSizeOptions={quotesPagination.pageSizeOptions}
            searchValue={quotesPagination.searchValue}
            searchPlaceholder="Search well name, API, or quote #..."
            isLoading={quotesLoading}
            isFetching={quotesFetching}
            onSearchChange={quotesPagination.setSearchValue}
            onPageChange={quotesPagination.setPageIndex}
            onPageSizeChange={quotesPagination.setPageSize}
            height="calc(100vh - 520px)"
          />
        </TabsContent>
      </Tabs>

      <div className="mt-6">
        <RecordMetadata record={project} />
      </div>

      <ProjectSegmentFormDialog
        open={segmentDialogOpen}
        onOpenChange={(open) => {
          setSegmentDialogOpen(open);
          if (!open) setEditingSegment(null);
        }}
        onSave={handleSaveSegment}
        editingSegment={editingSegment}
      />

      {id && project && (
        <AssignQuotesDialog
          open={assignQuotesOpen}
          onOpenChange={setAssignQuotesOpen}
          projectId={id}
          companyId={project.company_id}
        />
      )}

      {id && project && (
        <ManageSegmentsDialog
          open={manageSegmentsOpen}
          onOpenChange={setManageSegmentsOpen}
          projectId={id}
          projectName={project.name}
          existingSegments={segments}
        />
      )}
    </div>
  );
}

function SegmentCard({ segment, onClick }: { segment: ProjectSegmentWithRelations; onClick: () => void }) {
  return (
    <div className="bg-card border border-border rounded-lg p-5 cursor-pointer hover:border-primary/50 transition-colors" onClick={onClick}>
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <FileText className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h3 className="font-semibold text-foreground">
            {segment.list_segments?.display_value || 'Unknown Segment'}
          </h3>
          {segment.segment_manager_profile?.full_name && (
            <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
              <Users className="w-3 h-3" />
              <span>{segment.segment_manager_profile.full_name}</span>
            </div>
          )}
        </div>
      </div>
      {segment.notes && (
        <p className="text-sm text-muted-foreground line-clamp-3">{segment.notes}</p>
      )}
    </div>
  );
}
